/// <amd-module name="Test/Client/RestClient/Extended" />
import { IVssRestClient, RestClients } from "VSS/Platform/Context";
import { RestClientBase } from "VSS/Platform/RestClientBase";
import { deserializeVssJsonObject } from "VSS/Platform/Util/Serialization";
import { ShallowTestCaseResult, FieldDetailsForTestResults } from "Test/common-util/Contracts/Test";
import { TestCaseResultsWithContinuationToken, TestResultsFieldDetailsWithContinuationToken } from "Test/common-util/Contracts/ExtendedTest";

export interface IExtendedTestClient extends IVssRestClient {

    /**
        * [Preview API]
        *
        * @param {string} project - Project ID or project name
        * @param {number} buildId
        * @param {string} publishContext
        * @param {number} top
        * @param {string} continuationToken
        * @return Promise<TestCaseResultsWithContinuationToken>
        */
    getTestResultsByBuildWithContinuationToken(
        project: string,
        buildId: number,
        publishContext?: string,
        outcomes?: string,
        top?: number,
        continuationToken?: string): Promise<TestCaseResultsWithContinuationToken>;

    /**
     * [Preview API]
     *
     * @param {string} project - Project ID or project name
     * @param {number} releaseId
     * @param {number} releaseEnvid
     * @param {string} publishContext
     * @param {number} top
     * @param {string} continuationToken
     * @return Promise<TestCaseResultsWithContinuationToken>
     */
    getTestResultsByReleaseWithContinuationToken(
        project: string,
        releaseId: number,
        releaseEnvid?: number,
        publishContext?: string,
        outcomes?: string,
        top?: number,
        continuationToken?: string
    ): Promise<TestCaseResultsWithContinuationToken>;


    /**
     * @internal
     * [Preview API]
     *
     * @param {string} project - Project ID or project name
     * @param {number} buildId
     * @param {string} publishContext
     * @param {string[]} fields
     * @param {string} continuationToken
     * @return Promise<TestResultsFieldDetailsWithContinuationToken[]>
     */
    getResultGroupsByBuildWithContinuationToken(
        project: string,
        buildId: number,
        publishContext?: string,
        fields?: string[],
        continuationToken?: string
    ): Promise<TestResultsFieldDetailsWithContinuationToken>



    /**
 * @internal
 * [Preview API]
 *
 * @param {string} project - Project ID or project name
 * @param {number} releaseId
 * @param {string} publishContext
 * @param {number} releaseEnvId
 * @param {string[]} fields
 * @param {string} continuationToken
 * @return Promise<TestResultsFieldDetailsWithContinuationToken>
 */
    getResultGroupsByReleaseWithContinuationToken(
        project: string,
        releaseId: number,
        publishContext?: string,
        releaseEnvId?: number,
        fields?: string[],
        continuationToken?: string
    ): Promise<TestResultsFieldDetailsWithContinuationToken>
}

class ExtendedTestRestClient extends RestClientBase implements IExtendedTestClient {

    public async getTestResultsByBuildWithContinuationToken(
        project: string,
        buildId: number,
        publishContext?: string,
        outcomes?: string,
        top?: number,
        continuationToken?: string
    ): Promise<TestCaseResultsWithContinuationToken> {

        const queryValues: any = {
            buildId: buildId,
            publishContext: publishContext,
            outcomes:outcomes,
            "$top": top,
            continuationToken: continuationToken
        };

        return this.beginRequest<Response>({
            routeTemplate: "{project}/_apis/test/ResultsByBuild",
            routeValues: {
                project: project
            },
            queryParams: queryValues,
            apiVersion: "4.1-preview.1",
            returnRawResponse: true
        }).then((response: Response) => {
            const tokenString = response.headers.get(continuationTokenHeaderKey);
            const nextContinuationToken = tokenString;
            return response.text()
                .then((r) => {
                    return {
                        results: deserializeVssJsonObject<ShallowTestCaseResult[]>(r),
                        continuationToken: nextContinuationToken
                    } as TestCaseResultsWithContinuationToken;
                });
        });
    }

    public async getTestResultsByReleaseWithContinuationToken(
        project: string,
        releaseId: number,
        releaseEnvid?: number,
        publishContext?: string,
        outcomes?: string,
        top?: number,
        continuationToken?: string
    ): Promise<TestCaseResultsWithContinuationToken> {

        const queryValues: any = {
            releaseId: releaseId,
            releaseEnvid: releaseEnvid,
            publishContext: publishContext,
            outcomes:outcomes,
            "$top": top,
            continuationToken: continuationToken
        };

        return this.beginRequest<Response>({
            routeTemplate: "{project}/_apis/test/ResultsByRelease",
            routeValues: {
                project: project
            },
            queryParams: queryValues,
            apiVersion: "4.1-preview.1",
            returnRawResponse: true
        }).then((response: Response) => {
            const tokenString = response.headers.get(continuationTokenHeaderKey);
            const nextContinuationToken = tokenString;
            return response.text()
                .then((r) => {
                    return {
                        results: deserializeVssJsonObject<ShallowTestCaseResult[]>(r),
                        continuationToken: nextContinuationToken
                    } as TestCaseResultsWithContinuationToken;
                });
        });
    }

    getResultGroupsByBuildWithContinuationToken(
        project: string,
        buildId: number,
        publishContext: string,
        fields?: string[],
        continuationToken?: string
    ): Promise<TestResultsFieldDetailsWithContinuationToken> {

        const queryValues: any = {
            buildId: buildId,
            publishContext: publishContext,
            fields: fields && fields.join(","),
            continuationToken: continuationToken
        };

        return this.beginRequest<Response>({
            routeTemplate: "{project}/_apis/test/ResultGroupsByBuild",
            routeValues: {
                project: project
            },
            queryParams: queryValues,
            apiVersion: "5.0-preview.2",
            returnRawResponse: true
        }).then((response: Response) => {
            const tokenString = response.headers.get(continuationTokenHeaderKey);
            const nextContinuationToken = tokenString;
            return response.text()
                .then((r) => {
                    return {
                        fieldDetails: deserializeVssJsonObject<FieldDetailsForTestResults[]>(r),
                        continuationToken: nextContinuationToken
                    } as TestResultsFieldDetailsWithContinuationToken;
                });
        });
    }


    public getResultGroupsByReleaseWithContinuationToken(
        project: string,
        releaseId: number,
        publishContext: string,
        releaseEnvId?: number,
        fields?: string[],
        continuationToken?: string
    ): Promise<TestResultsFieldDetailsWithContinuationToken> {

        const queryValues: any = {
            releaseId: releaseId,
            publishContext: publishContext,
            releaseEnvId: releaseEnvId,
            fields: fields && fields.join(","),
            continuationToken: continuationToken
        };

        return this.beginRequest<Response>({
            routeTemplate: "{project}/_apis/test/ResultGroupsByRelease",
            routeValues: {
                project: project
            },
            queryParams: queryValues,
            apiVersion: "5.0-preview.2",
            returnRawResponse: true
        }).then((response: Response) => {
            const tokenString = response.headers.get(continuationTokenHeaderKey);
            const nextContinuationToken = tokenString;
            return response.text()
                .then((r) => {
                    return {
                        fieldDetails: deserializeVssJsonObject<FieldDetailsForTestResults[]>(r),
                        continuationToken: nextContinuationToken
                    } as TestResultsFieldDetailsWithContinuationToken;
                });
        });
    }
}

const continuationTokenHeaderKey = "x-ms-continuationtoken";

RestClients.add("IExtendedTestClient", {
    factory: ExtendedTestRestClient,
    options: {
        serviceInstanceType: "00025394-6065-48ca-87d9-7f5672854ef7"
    }
});


